'use client';

import * as React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Filter,
  Zap,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

import type { PreviewResult, ActionConfig, OperationFilters } from '../types';

interface ReviewPanelProps {
  filters: OperationFilters;
  action: ActionConfig;
  preview?: PreviewResult | null;
  isLoading: boolean;
}

const ACTION_LABELS: Record<string, string> = {
  processor_change: 'Change Processor',
  billing_update: 'Update Billing Date',
  distribute_rebills: 'Distribute Rebills',
  pause: 'Pause Subscriptions',
  resume: 'Resume Subscriptions',
  cancel: 'Cancel Subscriptions',
  retry_new_processor: 'Retry on New Processor',
  set_tagada_vault: 'Set Tagada Vault',
};

const INCOMPATIBILITY_LABELS: Record<string, string> = {
  apple_pay_cannot_migrate: 'Apple Pay cannot be migrated',
  google_pay_cannot_migrate: 'Google Pay cannot be migrated',
  paypal_external: 'PayPal managed externally',
  external_vault_no_migration: 'External vault cannot migrate',
  same_processor: 'Already on target processor',
  already_paused: 'Already paused',
  already_active: 'Already active',
  already_canceled: 'Already canceled',
  not_pausable: 'Cannot be paused',
  not_resumable: 'Cannot be resumed',
};

// Format filter items
function getFilterItems(filters: OperationFilters): Array<{ label: string; value: string }> {
  const items: Array<{ label: string; value: string }> = [];

  if (filters.statuses?.length) {
    items.push({ label: 'Status', value: filters.statuses.join(', ') });
  }
  if (filters.storeIds?.length) {
    items.push({ label: 'Stores', value: `${filters.storeIds.length} selected` });
  }
  if (filters.processorIds?.length) {
    items.push({ label: 'Processors', value: `${filters.processorIds.length} selected` });
  }
  if (filters.vaultType?.length) {
    items.push({ label: 'Vault', value: filters.vaultType.join(', ') });
  }
  if (filters.paymentType?.length) {
    items.push({ label: 'Payment', value: filters.paymentType.join(', ') });
  }
  if (filters.chainIndexValues?.length) {
    const formatted = filters.chainIndexValues.map(v => v === 'above3' ? '>3' : String(v)).join(', ');
    items.push({ label: 'Billing Cycle', value: formatted });
  }
  if (filters.retryCountValues?.length) {
    const formatted = filters.retryCountValues.map(v => v === 'above3' ? '>3' : String(v)).join(', ');
    items.push({ label: 'Retry Count', value: formatted });
  }
  if (filters.search) {
    items.push({ label: 'Search', value: `"${filters.search}"` });
  }

  return items;
}

// Format action details
function getActionDetails(action: ActionConfig): Array<{ label: string; value: string }> {
  const items: Array<{ label: string; value: string }> = [];

  switch (action.type) {
    case 'processor_change':
      if (action.targetProcessorId) {
        items.push({ label: 'Target', value: action.targetProcessorId });
      }
      if (action.migrateImmediately) {
        items.push({ label: 'Timing', value: 'Immediate' });
      }
      break;
    case 'billing_update':
      items.push({ label: 'Mode', value: action.billingMode.replace(/_/g, ' ') });
      if (action.newDate) {
        items.push({ label: 'Date', value: new Date(action.newDate).toLocaleDateString() });
      }
      break;
    case 'distribute_rebills':
      items.push({ label: 'Period', value: `${action.distributionPeriodDays} days` });
      if (action.updateAnchor) {
        items.push({ label: 'Anchor', value: 'Update permanently' });
      }
      break;
    case 'resume':
      items.push({ label: 'Behavior', value: action.resumeBehavior || 'reset' });
      if (action.resetDays !== undefined) {
        items.push({ label: 'Start', value: `In ${action.resetDays} days` });
      }
      break;
    case 'cancel':
      items.push({ label: 'When', value: action.cancelAtPeriodEnd ? 'At period end' : 'Immediately' });
      break;
    case 'retry_new_processor':
      if (action.targetProcessorId) {
        items.push({ label: 'Target', value: action.targetProcessorId });
      }
      if (action.resetRetryCounter) {
        items.push({ label: 'Reset retries', value: 'Yes' });
      }
      if (action.billImmediately) {
        items.push({ label: 'Bill', value: 'Immediately' });
      }
      break;
    case 'set_tagada_vault':
      items.push({ label: 'Vault', value: action.useTagadaVault ? 'Activate' : 'Deactivate' });
      break;
  }

  return items;
}

export function ReviewPanel({
  filters,
  action,
  preview,
  isLoading,
}: ReviewPanelProps) {
  const [incompatibleOpen, setIncompatibleOpen] = React.useState(false);

  const filterItems = getFilterItems(filters);
  const actionDetails = getActionDetails(action);

  return (
    <div className="space-y-5">
      {/* Summary Section */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left: Filters & Action */}
        <div className="space-y-4">
          {/* Filters */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters Applied</span>
            </div>
            <div className="bg-muted/40 rounded-md p-3 space-y-1.5">
              {filterItems.length > 0 ? (
                filterItems.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium truncate ml-2 max-w-[200px]" title={item.value}>
                      {item.value}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">No filters</div>
              )}
            </div>
          </div>

          {/* Action */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Action</span>
              <Badge variant="outline" className="ml-auto">
                {ACTION_LABELS[action.type]}
              </Badge>
            </div>
            {actionDetails.length > 0 && (
              <div className="bg-muted/40 rounded-md p-3 space-y-1.5">
                {actionDetails.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{item.label}</span>
                    <span className="font-medium truncate ml-2 max-w-[200px]" title={item.value}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Impact Stats */}
        <div>
          <div className="text-sm font-medium mb-2">Impact Summary</div>

          {isLoading ? (
            <div className="bg-muted/40 rounded-md p-4 space-y-3">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : preview ? (
            <div className="bg-muted/40 rounded-md p-4">
              {/* Main count */}
              <div className="text-3xl font-bold text-primary">
                {preview.compatibleCount.toLocaleString()}
              </div>
              <div className="text-sm text-muted-foreground mb-3">
                subscriptions will be affected
              </div>

              <Separator className="my-3" />

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-lg font-semibold text-green-600">
                    {preview.compatibleCount.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Compatible</div>
                </div>
                <div>
                  <div className={`text-lg font-semibold ${preview.incompatibleCount > 0 ? 'text-yellow-600' : 'text-muted-foreground'}`}>
                    {preview.incompatibleCount.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Skipped</div>
                </div>
                <div>
                  <div className="text-lg font-semibold">
                    {preview.targetCount.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-muted/40 rounded-md p-4 text-sm text-muted-foreground">
              Unable to calculate impact
            </div>
          )}
        </div>
      </div>

      {/* Incompatible subscriptions warning */}
      {!isLoading && preview && preview.incompatibleCount > 0 && (
        <Collapsible open={incompatibleOpen} onOpenChange={setIncompatibleOpen}>
          <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0" />
            <span className="text-sm text-yellow-800 flex-1">
              <strong>{preview.incompatibleCount}</strong> subscriptions will be skipped due to compatibility issues
            </span>
            <CollapsibleTrigger className="text-sm text-yellow-700 underline flex items-center gap-1 hover:text-yellow-900">
              Details
              <ChevronDown className={`h-3 w-3 transition-transform ${incompatibleOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent>
            <ScrollArea className="h-32 mt-2 rounded-md border bg-background p-2">
              <div className="space-y-1.5">
                {preview.incompatibleSubscriptions.map((sub) => (
                  <div
                    key={sub.subscriptionId}
                    className="flex items-center justify-between text-xs py-1"
                  >
                    <div className="flex items-center gap-2">
                      <XCircle className="h-3 w-3 text-red-500 shrink-0" />
                      <code className="text-muted-foreground">{sub.subscriptionId}</code>
                    </div>
                    <span className="text-muted-foreground truncate ml-2 max-w-[250px]">
                      {INCOMPATIBILITY_LABELS[sub.reason] || sub.reason}
                    </span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Confirmation */}
      {!isLoading && preview && preview.compatibleCount > 0 && (
        <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
          <div className="text-sm text-green-800">
            Ready to <strong>{ACTION_LABELS[action.type].toLowerCase()}</strong> on{' '}
            <strong>{preview.compatibleCount.toLocaleString()}</strong> subscriptions.
            This action can be rolled back.
          </div>
        </div>
      )}

      {/* No compatible subscriptions */}
      {!isLoading && preview && preview.compatibleCount === 0 && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>No compatible subscriptions</AlertTitle>
          <AlertDescription>
            None of the matched subscriptions can be processed. Go back and adjust filters or action.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
